// src/admin/pages/Products.tsx
import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { ProductList } from '../components/products/ProductList';
import { ProductForm } from '../components/products/ProductForm';
import { Plus, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Product } from '../../types';
import { adminApi, api } from '../../utils/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const Products: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [subcategories, setSubcategories] = useState<{[categoryId: number]: any[]}>({});

  const imageOperations = {
    getImages: async (productId: number) => {
      const response = await api.get<any>(`/admin/products/${productId}/images`, true);
      return response.data;
    },

    addImages: async (productId: number, formData: FormData) => {
      await api.post<any>(`/admin/products/${productId}/images`, formData, true);
    },

    deleteImage: async (productId: number, imageId: number) => {
      await api.delete<any>(`/admin/products/${productId}/images/${imageId}`, true);
    },

    reorderImages: async (productId: number, order: { imageId: number, sortOrder: number }[]) => {
      await api.put<any>(`/admin/products/${productId}/images/reorder`, { order }, true);
    },

    setMainImage: async (productId: number, imageId: number) => {
      await api.put<any>(`/admin/products/${productId}/images/${imageId}/main`, {}, true);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await adminApi.products.getAll();
        setProducts(response.data);
      } catch (err) {
        console.error('Error fetching products:', err);
        toast.error('Failed to fetch products');
      }
    };
    fetchProducts();
  }, []);

  const fetchSubcategories = async (categoryId: number) => {
    if (!categoryId) return [];
    
    if (subcategories[categoryId]) return subcategories[categoryId];
    
    try {
      const response = await adminApi.categories.getSubcategories(categoryId);
      const subcategoryData = response.data;
      
      setSubcategories(prev => ({
        ...prev,
        [categoryId]: subcategoryData
      }));
      
      return subcategoryData;
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error('Failed to load subcategories');
      return [];
    }
  };

  const handleEdit = async (product: Product) => {
    try {
      // Fetch full product details including variations for editing
      const response = await adminApi.products.getById(product.id.toString());
      const fullProduct = response.data;
      
      if (fullProduct.category_id) {
        await fetchSubcategories(fullProduct.category_id);
      }
      
      setEditingProduct(fullProduct);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching product details for editing:', err);
      toast.error('Failed to load product details for editing');
      
      // Fallback to the basic product data if the API call fails
      if (product.category_id) {
        await fetchSubcategories(product.category_id);
      }
      setEditingProduct(product);
      setShowForm(true);
    }
  };

  const handleDelete = async (productId: string | number) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await adminApi.products.delete(productId.toString());
        setProducts((prev) => prev.filter((p) => p.id !== productId));
        toast.success('Product deleted successfully');
      } catch (err) {
        console.error(err);
        toast.error('Error deleting product');
      }
    }
  };

  const handleSave = async (formData: FormData, editingId?: string | number) => {
    try {
      if (editingId) {
        await adminApi.products.update(editingId.toString(), formData);
        toast.success('Product updated successfully');
      } else {
        await adminApi.products.create(formData);
        toast.success('Product created successfully');
      }

      const response = await adminApi.products.getAll();
      setProducts(response.data);

      setShowForm(false);
      setEditingProduct(null);
    } catch (err: any) {
      console.error(err);
      toast.error(`Error saving product: ${err.message}`);
    }
  };

  const handleToggleStatus = async (productId: string | number, active: boolean) => {
    try {
      await adminApi.products.toggleStatus(productId.toString(), active);
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, active: active ? 1 : 0 } : p))
      );
      toast.success(`Product ${active ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error(err);
      toast.error('Error toggling product status');
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="h-screen flex flex-col">
        <div className="flex-none p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Products</h2>
            <button
              onClick={() => {
                setEditingProduct(null);
                setShowForm(true);
              }}
              className="bg-pink-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-pink-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add Product
            </button>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="relative max-w-md">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto px-6 pb-6">
            <ProductList
              products={filteredProducts}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
            />
          </div>
        </div>

        {showForm && (
          <ProductForm
            product={editingProduct}
            onSave={handleSave}
            onCancel={() => {
              setShowForm(false);
              setEditingProduct(null);
            }}
            fetchSubcategories={fetchSubcategories}
            subcategories={subcategories}
            imageOperations={imageOperations}
          />
        )}
      </div>
    </AdminLayout>
  );
};